import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MdHelpOutline, MdClose, MdSchool, MdEmail, MdNewReleases } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa6';
import { useLanguage } from '../i18n';
import DisplayPrefs from './DisplayPrefs';

// Help for every page: a short tip about where you are, a button to chat with
// support on WhatsApp, the welcome tour, and what is new. Lives in the top bar
// so it never covers anything (like the chat send button).
const SUPPORT_WHATSAPP = '250787525052';
const SUPPORT_EMAIL = 'info@mitifm.com';

const PAGE_TIPS = {
    dashboard: 'dashboard', 'work-orders': 'workOrders', assets: 'assets', spaces: 'spaces',
    maintenance: 'maintenance', inventory: 'inventory', requests: 'requests', bookings: 'bookings',
    payments: 'payments', reports: 'reports', messages: 'messages', users: 'users',
    settings: 'settings', billing: 'billing', 'my-tasks': 'myTasks',
};

export default function HelpCenter() {
    const { t } = useLanguage();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = event => { if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    const page = (location.pathname.split('/')[2] || '').toLowerCase();
    const tipKey = PAGE_TIPS[page] || 'default';
    const whatsappUrl = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(t('help.whatsappMsg') + (page ? `(${page}) ` : ''))}`;

    const row = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff', color: '#0f172a', textDecoration: 'none', fontSize: 14, cursor: 'pointer' };

    return (
        <div ref={boxRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button type="button" onClick={() => setOpen(o => !o)} aria-label={t('help.open')} aria-expanded={open} title={t('help.title')}
                style={{ border: '1px solid #e2e8f0', background: open ? '#eff6ff' : 'transparent', borderRadius: 8, height: 36, padding: '0 10px', display: 'inline-flex', alignItems: 'center', gap: 6, color: '#0369a1', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                <MdHelpOutline size={20} /> <span className="d-none d-lg-inline">{t('help.title')}</span>
            </button>
            {open && (
                <div role="dialog" aria-label={t('help.title')} style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 340, maxWidth: 'calc(100vw - 24px)', maxHeight: '78vh', overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, boxShadow: '0 18px 40px rgba(15,23,42,.2)', padding: 16, zIndex: 3000, textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <strong style={{ fontSize: 16 }}>{t('help.title')}</strong>
                        <button type="button" onClick={() => setOpen(false)} aria-label={t('help.close')} style={{ border: 0, background: 'none', color: '#64748b', cursor: 'pointer' }}><MdClose size={20} /></button>
                    </div>

                    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', color: '#0369a1', marginBottom: 4 }}>{t('help.thisPage')}</div>
                        <div style={{ fontSize: 13.5, lineHeight: 1.55, color: '#0f172a' }}>{t(`help.tip.${tipKey}`)}</div>
                    </div>

                    <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
                        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" style={{ ...row, background: '#25d366', border: '1px solid #25d366', color: '#04230f', fontWeight: 700 }}>
                            <FaWhatsapp size={20} /> {t('help.whatsapp')}
                        </a>
                        <button type="button" style={row} onClick={() => { setOpen(false); window.dispatchEvent(new Event('fms:start-tour')); }}>
                            <MdSchool size={20} color="#0369a1" /> {t('help.tour')}
                        </button>
                        <a href={`mailto:${SUPPORT_EMAIL}`} style={row}><MdEmail size={20} color="#0369a1" /> {t('help.email')}</a>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <DisplayPrefs compact />
                    </div>

                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', color: '#64748b', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MdNewReleases size={14} /> {t('help.whatsNew')}
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55, color: '#334155' }}>
                        {[1, 2, 3, 4, 5].map(n => <li key={n} style={{ marginBottom: 4 }}>{t(`help.new${n}`)}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}
